import { PatientLayout } from "@/components/layout/patient-layout";
import { useGetPatientDashboard } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "wouter";
import {
  Info,
  Link as LinkIcon,
  ArrowRight,
  BookHeart,
  BookOpen,
  Pill,
  Activity,
  FileText,
  CalendarDays,
  Clock,
  Stethoscope,
  ExternalLink,
  X,
} from "lucide-react";
import { PromotionBanners } from "@/components/ui/promotion-banner";
import { useRecentlyViewed } from "@/hooks/use-recently-viewed";
import { useOldestChatDeletionCountdown } from "@/lib/retention";
import { useState } from "react";

// ---------------------------------------------------------------------------
// Appointments shortcut (#43)
// ---------------------------------------------------------------------------

const APPT_KEY = "rheumhub_patient_next_appointment";

interface ApptDetails {
  date: string;
  time: string;
  location: string;
  notes: string;
}

function AppointmentsCard() {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState<ApptDetails | null>(() => {
    try {
      const raw = localStorage.getItem(APPT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [form, setForm] = useState<ApptDetails>(
    saved ?? { date: "", time: "", location: "", notes: "" }
  );

  const save = () => {
    const data: ApptDetails = form;
    localStorage.setItem(APPT_KEY, JSON.stringify(data));
    setSaved(data);
    setEditing(false);
  };

  const clear = () => {
    localStorage.removeItem(APPT_KEY);
    setSaved(null);
    setForm({ date: "", time: "", location: "", notes: "" });
    setEditing(false);
  };

  const hasContent = saved && (saved.date || saved.location || saved.notes);

  return (
    <div className="bg-sky-50 rounded-3xl p-6 border border-sky-100">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white text-sky-500 flex items-center justify-center shadow-sm shrink-0">
            <CalendarDays className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900">Your Next Appointment</h3>
            <p className="text-xs text-sky-600">Rheumatology team</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasContent && !editing && (
            <button
              onClick={clear}
              className="text-slate-300 hover:text-slate-500 transition-colors"
              title="Clear appointment"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => {
              if (saved) setForm(saved);
              setEditing(e => !e);
            }}
            className="text-xs font-medium text-sky-700 hover:text-sky-900 bg-white px-3 py-1 rounded-full border border-sky-200 hover:border-sky-300 transition-colors"
          >
            {editing ? "Cancel" : hasContent ? "Edit" : "Add details"}
          </button>
        </div>
      </div>

      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="appt-date" className="text-xs font-medium text-slate-600 block mb-1">Date</label>
              <input
                id="appt-date"
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white"
              />
            </div>
            <div>
              <label htmlFor="appt-time" className="text-xs font-medium text-slate-600 block mb-1">Time</label>
              <input
                id="appt-time"
                type="time"
                value={form.time}
                onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white"
              />
            </div>
          </div>
          <div>
            <label htmlFor="appt-location" className="text-xs font-medium text-slate-600 block mb-1">Hospital / clinic</label>
            <input
              id="appt-location"
              type="text"
              placeholder="e.g. Royal National Hospital, Rheumatology Clinic A"
              value={form.location}
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white"
            />
          </div>
          <div>
            <label htmlFor="appt-notes" className="text-xs font-medium text-slate-600 block mb-1">Notes (optional)</label>
            <input
              id="appt-notes"
              type="text"
              placeholder="e.g. Bring blood test results, fasting required"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white"
            />
          </div>
          <button
            onClick={save}
            className="w-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-xl py-2 transition-colors"
          >
            Save appointment
          </button>
        </div>
      ) : hasContent ? (
        <div className="space-y-2 text-sm">
          {saved!.date && (
            <div className="flex items-center gap-2 text-slate-700">
              <CalendarDays className="h-4 w-4 text-sky-400 shrink-0" />
              <span className="font-medium">
                {new Date(saved!.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                {saved!.time ? ` at ${saved!.time}` : ""}
              </span>
            </div>
          )}
          {saved!.location && (
            <div className="flex items-center gap-2 text-slate-600">
              <Stethoscope className="h-4 w-4 text-sky-400 shrink-0" />
              <span>{saved!.location}</span>
            </div>
          )}
          {saved!.notes && (
            <div className="flex items-start gap-2 text-slate-500 text-xs mt-1">
              <Info className="h-3.5 w-3.5 text-sky-300 shrink-0 mt-0.5" />
              <span>{saved!.notes}</span>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-500">
          Save your next appointment date and location here for quick reference.
        </p>
      )}

      <div className="mt-4 pt-3 border-t border-sky-100 flex flex-wrap gap-3">
        <a
          href="https://www.nhs.uk/nhs-app/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:text-sky-900 transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          View appointments on NHS App
        </a>
        <a
          href="https://www.nhs.uk/service-search/find-a-service/rheumatology"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:text-sky-900 transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Find rheumatology near you
        </a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recently viewed widget (#44)
// ---------------------------------------------------------------------------

function RelTime({ iso }: { iso: string }) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHrs = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHrs / 24);
  let label = "just now";
  if (diffMins >= 1 && diffMins < 60) label = `${diffMins}m ago`;
  else if (diffHrs >= 1 && diffHrs < 24) label = `${diffHrs}h ago`;
  else if (diffDays === 1) label = "yesterday";
  else if (diffDays > 1) label = `${diffDays} days ago`;
  return <span>{label}</span>;
}

function RecentlyViewedWidget() {
  const { items } = useRecentlyViewed();
  if (items.length === 0) return null;

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Clock className="h-5 w-5 text-slate-400" />
          Continue reading
        </h2>
      </div>
      <div className="flex flex-wrap gap-3">
        {items.map(item => (
          <Link key={`${item.type}-${item.id}`} href={item.href}>
            <div className={`flex items-center gap-3 rounded-2xl border px-4 py-3 cursor-pointer transition-all hover:shadow-md group ${
              item.type === "condition"
                ? "bg-rose-50 border-rose-100 hover:border-rose-200"
                : "bg-teal-50 border-teal-100 hover:border-teal-200"
            }`}>
              <div className={`shrink-0 ${item.type === "condition" ? "text-rose-400" : "text-teal-500"}`}>
                {item.type === "condition"
                  ? <BookOpen className="h-4 w-4" />
                  : <Pill className="h-4 w-4" />
                }
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate max-w-[160px] group-hover:text-rose-700 transition-colors">
                  {item.name}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  <RelTime iso={item.viewedAt} />
                </p>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all shrink-0" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Oldest-chat retention countdown (#59)
// ---------------------------------------------------------------------------

function OldestChatCountdownBanner() {
  const countdown = useOldestChatDeletionCountdown();
  if (!countdown) return null;
  return (
    <div className="mb-10">
      <Link href="/patient/settings">
        <div
          className="flex items-center gap-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-2xl px-4 py-3 cursor-pointer transition-colors"
          title={`Scheduled deletion: ${countdown.absoluteLabel}`}
        >
          <div className="h-9 w-9 rounded-xl bg-white text-slate-500 flex items-center justify-center shadow-sm shrink-0">
            <Clock className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-700">
              Your oldest chat will be deleted{" "}
              <span className="font-semibold text-slate-900">{countdown.relativePhrase}</span>
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {countdown.absoluteLabel} · adjust in privacy settings
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
        </div>
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export default function PatientDashboard() {
  const { data: dashboard, isLoading, error } = useGetPatientDashboard();

  if (isLoading) return <PatientLayout title="Your Dashboard"><LoadingScreen /></PatientLayout>;
  if (error || !dashboard) return <PatientLayout title="Your Dashboard"><div>Error loading dashboard. Please try again.</div></PatientLayout>;

  return (
    <PatientLayout title="Welcome to RheumHub">
      <PromotionBanners audience="patient" />
      <div className="mb-8 max-w-3xl">
        <p className="text-lg text-slate-600 leading-relaxed">
          Your personal guide to understanding rheumatology. We're here to provide clear, reassuring information about your condition and treatments.
        </p>
      </div>

      {/* #44 Recently viewed — shows only once something has been read */}
      <RecentlyViewedWidget />

      {/* #43 Appointments shortcut — always visible */}
      <div className="mb-10">
        <AppointmentsCard />
      </div>

      {/* #59 Oldest-chat retention countdown — only when patient has chats */}
      <OldestChatCountdownBanner />

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <div className="bg-rose-50 rounded-3xl p-8 border border-rose-100 flex flex-col justify-between h-full hover:shadow-sm transition-shadow">
          <div>
            <div className="h-12 w-12 rounded-full bg-white text-rose-500 flex items-center justify-center mb-6 shadow-sm">
              <Info className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Learn about conditions</h2>
            <p className="text-slate-700 mb-6">
              Read plain-language explanations of rheumatic conditions, symptoms, and what to expect.
            </p>
          </div>
          <Link href="/patient/conditions">
            <span className="inline-flex items-center text-rose-700 font-medium hover:text-rose-800 transition-colors group">
              Browse conditions <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        </div>

        <div className="bg-amber-50 rounded-3xl p-8 border border-amber-100 flex flex-col justify-between h-full hover:shadow-sm transition-shadow">
          <div>
            <div className="h-12 w-12 rounded-full bg-white text-amber-500 flex items-center justify-center mb-6 shadow-sm">
              <BookHeart className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Trusted Resources</h2>
            <p className="text-slate-700 mb-6">
              Access verified support links, charities, and official NHS guidance all in one place.
            </p>
          </div>
          <Link href="/patient/resources">
            <span className="inline-flex items-center text-amber-700 font-medium hover:text-amber-800 transition-colors group">
              Find resources <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        </div>

        <div className="bg-pink-50 rounded-3xl p-8 border border-pink-100 flex flex-col justify-between h-full hover:shadow-sm transition-shadow">
          <div>
            <div className="h-12 w-12 rounded-full bg-white text-pink-500 flex items-center justify-center mb-6 shadow-sm">
              <BookOpen className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Know Your Condition</h2>
            <p className="text-slate-700 mb-6">
              In-depth plain-language guides to your specific diagnosis — what it means, what to expect, and how to live well with it.
            </p>
          </div>
          <Link href="/patient/know-your-condition">
            <span className="inline-flex items-center text-pink-700 font-medium hover:text-pink-800 transition-colors group">
              Read condition guides <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        </div>

        <div className="bg-teal-50 rounded-3xl p-8 border border-teal-100 flex flex-col justify-between h-full hover:shadow-sm transition-shadow">
          <div>
            <div className="h-12 w-12 rounded-full bg-white text-teal-500 flex items-center justify-center mb-6 shadow-sm">
              <Pill className="h-6 w-6" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Know Your Medication</h2>
            <p className="text-slate-700 mb-6">
              Clear guides to the medicines used in rheumatology — what they do, how to take them, and what to watch for.
            </p>
          </div>
          <Link href="/patient/know-your-medication">
            <span className="inline-flex items-center text-teal-700 font-medium hover:text-teal-800 transition-colors group">
              Read medication guides <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        </div>
      </div>

      {/* Triage / Symptom Check card */}
      <div className="mb-6 bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl border border-red-100 p-8 flex flex-col sm:flex-row sm:items-center gap-6">
        <div className="h-14 w-14 rounded-2xl bg-white text-red-500 flex items-center justify-center shrink-0 shadow-sm">
          <Activity className="h-7 w-7" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Not sure what to do about your symptoms?</h2>
          <p className="text-slate-600 text-sm leading-relaxed">
            Use our Symptom Check tool to get guidance on whether to call 999, NHS 111, your GP, or your rheumatology nurse. Also includes flare assessment and pain support.
          </p>
        </div>
        <Link href="/patient/triage" className="shrink-0">
          <span className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-full px-6 py-3 text-sm transition-colors whitespace-nowrap">
            <Activity className="h-4 w-4" />
            Symptom Check
          </span>
        </Link>
      </div>

      {/* Contact Advice Line card */}
      <div className="mb-12 bg-gradient-to-br from-violet-50 to-teal-50 rounded-3xl border border-violet-100 p-8 flex flex-col sm:flex-row sm:items-center gap-6">
        <div className="h-14 w-14 rounded-2xl bg-white text-violet-500 flex items-center justify-center shrink-0 shadow-sm">
          <FileText className="h-7 w-7" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Need a prescription or blood form?</h2>
          <p className="text-slate-600 text-sm leading-relaxed">
            Submit a structured request to your rheumatology advice line — for repeat prescriptions, medication queries, or blood test forms.
          </p>
        </div>
        <Link href="/patient/requests" className="shrink-0">
          <span className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-full px-6 py-3 text-sm transition-colors whitespace-nowrap">
            <FileText className="h-4 w-4" />
            Contact Advice Line
          </span>
        </Link>
      </div>

      <div className="mb-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Common Conditions</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {dashboard.featuredConditions.map(c => (
            <Link key={c.id} href={`/patient/conditions/${c.id}`}>
              <Card className="h-full hover:border-rose-300 hover:shadow-md transition-all cursor-pointer rounded-2xl">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-rose-600 transition-colors">{c.name}</h3>
                  <p className="text-sm text-slate-500 mb-4">{c.simpleName}</p>
                  <span className="text-sm font-medium text-rose-600 inline-flex items-center">
                    Read more <ArrowRight className="h-3 w-3 ml-1" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Helpful Links</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {dashboard.featuredResources.map(r => (
            <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer" className="block">
              <Card className="hover:border-amber-300 hover:shadow-md transition-all rounded-2xl h-full">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="mt-1">
                    <LinkIcon className="h-5 w-5 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-1">{r.title}</h3>
                    <p className="text-sm text-slate-500">{r.organisation}</p>
                  </div>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </PatientLayout>
  );
}
